import React, { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { APP_URLS } from 'src/constants';
import { Modal, BareModal, PrimaryButton, Button } from '@cb/apricot-react';

import { Radio, Checkbox, Input } from 'src/generic/component/form';
import { ISummary } from 'src/types';
import { IAIA, IAIARequest } from 'src/types/org/aia';
import { ITestingPlan } from 'src/types/org/common';

import { FieldRow, FieldLabel, Panel, ErrorNotice, ContainerInline, HeaderInline, Loader } from 'src/generic/component';
import { ValidationError, IFieldErrors, ErrorManager, ERRORS_EMPTY } from 'src/types/validation';
import { IRequestMeta } from 'src/types/org/request';
import { AIAFields } from '..';
import { TestingPlanFields } from '../common/testingPlanForm';
import { Draft } from 'src/types/common';
import { saveAIForm, submitAIForm } from 'src/apis';
import { validate as aiaValidate } from 'src/types/org/aia/aiaSchema';
import { validate as testingPlanValidate } from 'src/types/org/common/testingPlanSchema';
import { validate } from 'src/types/org/aia/requestSchema';

interface IAIARequestProps {
  item: ISummary;
  request: Draft<IAIARequest>;
  readOnly?: boolean;
  onClose: () => void;
}

export const AIARequestForm: FC<IAIARequestProps> = ({ item, request: initial, readOnly, onClose }) => {
  const navigate = useNavigate();

  const [request, setRequest] = useState<Draft<IAIARequest>>(initial || {});
  const [step, setStep] = useState('aia');

  const [appliedLevel, setAppliedLevel] = useState<'1' | '2' | undefined>(initial?.appliedLevel);
  const [aia, setAia] = useState<Draft<IAIA>>(initial?.data?.aia || {});
  const [testingPlan, setTestingPlan] = useState<Draft<ITestingPlan>>(initial?.data?.testingPlan || {});
  const metaInitial = {
    creator: initial?.meta?.creator || item.creator,
    creatorEmail: initial?.meta?.creatorEmail || item.creatorEmail,
    submitter: readOnly ? initial?.meta?.submitter : undefined,
    disclaimer: readOnly ? initial?.meta?.disclaimer : undefined
  };
  const [meta, setMeta] = useState<Draft<IRequestMeta>>(metaInitial);

  const draft: Draft<IAIARequest> = {
    userId: item.userId,
    categoryId: item.categoryId,
    appliedLevel: appliedLevel,
    data: {
      aia,
      testingPlan
    },
    meta
  };

  let aiaCompleted = false;
  try {
    aiaValidate(appliedLevel || '1', aia);
    aiaCompleted = true;
  } catch (error) { }

  let testingPlanCompleted = false;
  try {
    testingPlanValidate(testingPlan);
    testingPlanCompleted = true;
  } catch (error) { }

  const requireTestingPlan = appliedLevel === '2';

  useEffect(() => {
    setRequest(initial || {});
    setAppliedLevel(initial?.appliedLevel);
    setAia(initial?.data?.aia || {});
    setTestingPlan(initial?.data?.testingPlan || {});
    //If form is still able to be editted, we want to force a sign-off one every new submission
    setMeta(metaInitial);
  }, [initial]);

  const [validating, setValidating] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string | React.ReactNode>('');
  const [errorMessageOpen, setErrorMessageOpen] = useState<boolean>(false);
  const [aiaErrors, setAIAErrors] = useState<IFieldErrors>(ERRORS_EMPTY);
  const [testingPlanErrors, setTestingPlanErrors] = useState<IFieldErrors>(ERRORS_EMPTY);
  const [metaErrors, setMetaErrors] = useState<IFieldErrors>(ERRORS_EMPTY);
  const [levelError, setLevelError] = useState<string>('');
  const { getError: getMetaError } = ErrorManager(metaErrors);

  const validateLevel = (appliedLevel: '1' | '2' | undefined) => {
    setLevelError('');
    if (!appliedLevel) {
      const error = `Applied level must be either '1' or '2'`;
      setLevelError(error);
      throw new ValidationError({
        _errors: [error]
      });
    }
    return appliedLevel;
  };

  const validateAia = (appliedLevel: '1' | '2' | undefined, data: Draft<IAIA>) => {
    try {
      const level = validateLevel(appliedLevel);
      const validated = aiaValidate(level, data);
      setAIAErrors(ERRORS_EMPTY);
      return validated;
    } catch (error: any) {
      if (error instanceof ValidationError) {
        setAIAErrors(error.fieldErrors);
      } else {
        setErrorMessage(error);
      }
    }
  };

  const validateTestingPlan = (data: Draft<ITestingPlan>) => {
    try {
      const validated = testingPlanValidate(data);
      setTestingPlanErrors(ERRORS_EMPTY);
      return validated;
    } catch (error: any) {
      if (error instanceof ValidationError) {
        setTestingPlanErrors(error.fieldErrors);
      } else {
        setErrorMessage(error);
      }
    }
  };

  const handleSave = async () => {
    setSubmitting(true);
    try {
      const response = await saveAIForm(draft);
      setErrorMessage('');
      setErrorMessageOpen(false);
      setSubmitting(false);
      setRequest(draft);
      return response;
    } catch (error: any) {
      let message = "An unknown server error occurred, the save was unsuccessful.";
      if (error instanceof ValidationError) {
        const { getFieldError } = ErrorManager(error.fieldErrors);
        setAIAErrors(getFieldError('data.aia'));
        setTestingPlanErrors(getFieldError('data.testingPlan'));
        setValidating(true);
        message = "The save failed as some of the entered fields are invalid.";
      } else {
        console.log(error?.message || error);
      }
      setErrorMessageOpen(true);
      setErrorMessage(message);
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    setValidating(true);
    setSubmitting(true);
    try {
      const validated = validate(draft);
      if (validated)
        await submitAIForm(validated);
      navigate(APP_URLS.HOME, {state: {type: 'ai'}});
    } catch (error: any) {
      if (error instanceof ValidationError) {
        const { getFieldError } = ErrorManager(error.fieldErrors);
        setAIAErrors(getFieldError('data.aia'));
        setTestingPlanErrors(getFieldError('data.testingPlan'));
        setMetaErrors(getFieldError('meta'));
      } else {
        setErrorMessage(error);
      }
    }
    setSubmitting(false);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    setErrorMessageOpen(false);
    setErrorMessage(false);
  }, [step]);

  const scrollToError = () => {
    const element = document.querySelector<HTMLElement>('.cb-validation-error');
    if (element) {
      const top = element.getBoundingClientRect().top;
      window.scrollBy({ top: top - 100 });
    }
  };

  const handleNext = async () => {
    let isValid = false;
    let nextStep = 'submit';
    if (step === 'aia') {
      nextStep = appliedLevel === '2' ? 'testingPlan' : 'submit';
    }

    if (!readOnly) {
      const validated = (step === 'aia') ? validateAia(appliedLevel, draft.data?.aia || {}) : validateTestingPlan(draft.data?.testingPlan || {});
      if (validated) {
        if (await handleSave()) {
          isValid = true;
        }
      } else {
        setValidating(true);
        setErrorMessageOpen(true);
        setErrorMessage(<div>There were invalid fields still in the form. Look for errors near your fields.</div>);
      }
    } else {
      isValid = true;
    }

    if (isValid) setStep(nextStep);
  };

  const SaveButton: FC = () => {
    return <PrimaryButton style={{ marginBottom: '10px', width: '100%' }} onClick={async () => {
      await handleSave();
    }} disabled={submitting}>Save</PrimaryButton>;
  };

  const CloseButton: FC = () => {
    return <Button style={{ marginBottom: '10px', width: '100%' }} onClick={() => {
      onClose();
    }} disabled={submitting}>Close</Button>;
  };

  let breadcrumb = 'aia-breadcrumb';
  let submitBreadcrumb = breadcrumb;
  let testingPlanBreadcrumb = breadcrumb;
  let aiaBreadcrumb = breadcrumb;

  if (step === 'aia') {
    aiaBreadcrumb += ' active';
  }

  if (step === 'testingPlan') {
    testingPlanBreadcrumb += ' active';
  } else if (!aiaCompleted) {
    testingPlanBreadcrumb += ' disabled';
  }

  const submitDisabled = (requireTestingPlan && !testingPlanCompleted) || !aiaCompleted;

  if (submitDisabled) {
    submitBreadcrumb += ' disabled';
  } else if (step === 'submit') {
    submitBreadcrumb += ' active';
  }

  let form = <Loader />;
  if (request) {
    if (step === 'aia') {
      form = <>
        <Panel>
          <FieldLabel style={{ marginBottom: '4px' }} variant="body1">School Code Level</FieldLabel>
          <Radio name='appliedLevel' readOnly={readOnly} onChange={(val: any) => { setAppliedLevel(val); }} value={appliedLevel || ''} error={levelError}
            options={[{ key: '1', label: 'Level 1: I want to become a score recipient' }, { key: '2', label: 'Level 2: I want to become a score recipient and administer assessments' }]}
          />
        </Panel>
        <AIAFields appliedLevel={appliedLevel} categoryId={item.categoryId} userId={item.userId} item={aia} readOnly={readOnly} errors={aiaErrors}
          onUpdate={async (data) => {
            if (validating) {
              validateAia(appliedLevel, data);
            }
            setAia(data);
          }} />
        <br />
        {!readOnly && <SaveButton />}
        {readOnly && <CloseButton />}
        <PrimaryButton style={{ marginRight: '10px', width: '100%' }} onClick={handleNext} disabled={submitting}>Next</PrimaryButton>
      </>;
    }
    if ((step === 'testingPlan' && appliedLevel === '2')) {
      form = <><TestingPlanFields categoryId={item.categoryId} userId={item.userId} item={testingPlan} readOnly={readOnly} errors={testingPlanErrors}
        onUpdate={async (data) => {
          if (validating) {
            validateTestingPlan(data);
          }
          setTestingPlan(data);
        }} />
        <br />
        {!readOnly && <SaveButton />}
        {readOnly && <CloseButton />}

        <PrimaryButton style={{ width: '100%' }} onClick={handleNext} disabled={submitting}>Next</PrimaryButton>
      </>;
    } else if (step === 'submit') {
      form = <>
        <Panel>
          <h4 style={{ marginBottom: "20px" }}>Disclaimer</h4>
          <FieldRow>
            <FieldLabel variant="body1" style={{ marginBottom: '5px' }}>By submitting this request I confirm that all of the information provided is true and accurately describes the organization named on this form and have the authority to make this request. I understand that if any of the information is false, deactivation of the school code and/or legal action may result.</FieldLabel>
            <Checkbox disabled={readOnly} label='Accept' style={{ display: 'inline-block' }} checked={meta.disclaimer || false} onChange={() => {
              setMeta({ ...meta, disclaimer: meta.disclaimer ? !meta.disclaimer : true });
            }} />
            {getMetaError('disclaimer') &&
              <span className='cb-input-helper cb-validation-error'>You must agree to the disclaimer</span>
            }
          </FieldRow>

          <FieldRow>
            <FieldLabel variant="body1">Submitter</FieldLabel>
            <Input
              disabled={readOnly}
              validation={getMetaError('submitter') ? 'error' : undefined}
              validationMsg={getMetaError('submitter')}
              onChange={(e: any) => { setMeta({ ...meta, submitter: e.target.value }); }} value={meta.submitter} />
          </FieldRow>
        </Panel>
        <br />
        {!readOnly &&
          <PrimaryButton style={{ width: '100%' }} onClick={async () => {
            await handleSubmit();
          }} disabled={submitting}>Submit</PrimaryButton>
        }
        {readOnly && <CloseButton />}
      </>;
    }
  }

  return (<div>
    {/* @ts-ignore  */}
    <Modal withHeader={false} withFooter={true}
      open={errorMessageOpen} onClose={() => { setErrorMessageOpen(false); scrollToError(); }}>
      <ContainerInline>
        <HeaderInline style={{ marginBottom: '6px' }}>
          <i style={{ marginRight: '10px' }} className="cb-icon cb-icon-circular cb-exclamation cb-red1-color" aria-hidden="true"></i>
          <h4 className="cb-notification-title"><strong>Errors Occurred</strong></h4>
        </HeaderInline>
        <div style={{ marginLeft: '42px' }} className="cb-notification-content">
          {errorMessage}
        </div>
      </ContainerInline>
    </Modal>

    {/* @ts-ignore */}
    <BareModal open={submitting} withFooter={false} withHeader={false}>
      <Loader />
    </BareModal>

    <div style={{ display: 'flex' }}>
      <div className={aiaBreadcrumb} style={{ marginRight: '10px' }} onClick={() => { setStep('aia'); }}>Institution Info</div> &gt;
      {requireTestingPlan &&
        <><div className={testingPlanBreadcrumb} style={{ marginRight: '10px', marginLeft: '10px' }} onClick={() => {
          if (aiaCompleted)
            setStep('testingPlan');
        }}>Testing Plan</div>&gt;</>
      }
      <div className={submitBreadcrumb} style={{ marginRight: '10px', marginLeft: '10px' }} onClick={() => {
        if (!submitDisabled)
          setStep('submit');
      }}>Submitter Info</div>
    </div>

    {errorMessage &&
      <div style={{ marginBottom: '20px', marginTop: '20px' }}>
        <ErrorNotice errorMessage={errorMessage} />
      </div>
    }

    {form}
  </div>);
};